import { ref } from 'vue';
import { getHomepagePreviewConfig } from '@/config/homepagePreview.js';

export function useHomepagePreview() {
  const { origin: previewOrigin, url: previewUrl } = getHomepagePreviewConfig();
  const ready = ref(previewOrigin ? false : 'error');
  let iframeEl = null;
  let readyTimeout = null;
  let latestSections = null;
  let latestSelectedKey = null;
  let onSectionClick = null;
  let loadingAcknowledged = false;

  function clearReadyTimeout() {
    clearTimeout(readyTimeout);
    readyTimeout = null;
  }

  function send(type, payload = {}) {
    if (!previewOrigin) return;
    iframeEl?.contentWindow?.postMessage({ source: 'homepage-builder', type, payload }, previewOrigin);
  }

  function sendDraft() {
    if (ready.value !== true || latestSections === null) return;
    // Vue/Pinia proxies cannot be structured-cloned. Send rendering fields
    // only, preserving the editor's current array order after drag-and-drop.
    const sections = JSON.parse(JSON.stringify(latestSections.map((section, index) => ({
      key: section.key,
      type: section.type,
      is_active: section.is_active,
      sort_order: index,
      settings: section.settings,
    }))));
    send('draft-update', { sections });
  }

  function sendSelection() {
    if (ready.value === true) send('select-section', { key: latestSelectedKey });
  }

  function handleMessage(event) {
    if (!iframeEl || event.source !== iframeEl.contentWindow || event.origin !== previewOrigin) return;
    const msg = event.data;
    if (!msg || msg.source !== 'homepage-builder') return;

    if (msg.type === 'preview-loading') {
      loadingAcknowledged = true;
      ready.value = false;
      clearReadyTimeout();
    } else if (msg.type === 'preview-ready') {
      ready.value = true;
      clearReadyTimeout();
      sendDraft();
      sendSelection();
    } else if (msg.type === 'preview-error') {
      onFrameError();
    } else if (msg.type === 'section-click' && ready.value === true) {
      onSectionClick?.(msg.payload?.key ?? null);
    }
  }

  function onFrameLoad() {
    clearReadyTimeout();
    // Recover a handshake missed during mounting or an explicit iframe reload.
    send('preview-connect');
    if (ready.value === true || loadingAcknowledged) return;
    // Only a fully loaded document with no preview protocol response is an
    // error. An acknowledged preview remains loading while its API is pending.
    readyTimeout = setTimeout(() => {
      if (ready.value !== true && !loadingAcknowledged) onFrameError();
    }, 10000);
  }

  function onFrameError() {
    clearReadyTimeout();
    ready.value = 'error';
  }

  function updateSections(sections) {
    latestSections = sections;
    sendDraft();
  }

  function updateSelection(key) {
    latestSelectedKey = key;
    sendSelection();
  }

  function attach(el) {
    if (iframeEl === el) return;
    iframeEl = el;
    ready.value = previewOrigin ? false : 'error';
    loadingAcknowledged = false;
    clearReadyTimeout();
  }

  window.addEventListener('message', handleMessage);
  function dispose() {
    clearReadyTimeout();
    window.removeEventListener('message', handleMessage);
  }

  return {
    previewOrigin, previewUrl, ready, attach, onFrameLoad, onFrameError,
    updateSections, updateSelection, dispose,
    setSectionClickHandler(fn) { onSectionClick = fn; },
  };
}